import cn from 'classnames';
import React from 'react';
import { BodyCell } from '@dealroadshow/uikit/core/components/Table/DataTable';
import IconExternalLink from '@dealroadshow/uikit/core/components/Icon/IconExternalLink';
import IconFileBrackets from '@dealroadshow/uikit/core/components/Icon/IconFileBrackets';
import IconFileTable from '@dealroadshow/uikit/core/components/Icon/IconFileTable';
import IconFileTxt2 from '@dealroadshow/uikit/core/components/Icon/IconFileTxt2';
import Cell from './Cell';
import { getFile, getFileName, getRelatedDocument, isConvertible } from './helpers';
import { IFilling } from '@/finsight/domain/vo/Filings/Filings';

import filingsCellsStyles from './filingsCells.scss';
import spacesStyles from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';

const getCsvCell = ({ row: filing }: { row: IFilling }) => {
  const contentCell = <IconFileTable />;
  const cellProps = {
    tooltip: 'Related Datatape is not available',
    href: '',
    className: filingsCellsStyles.downloadLink,
    content: <span className={ filingsCellsStyles.disabled }>{ contentCell }</span>,
  };
  const file = getFile(filing);

  if (Object.keys(file).length) {
    if (isConvertible(file)) {
      cellProps.tooltip = 'Download Data Tape (CSV)';
      cellProps.content = (
        <a
          href={ filing.xml2csvUrlTpl?.replace('{id}', file.fileViewId) }
          /* eslint-disable-next-line react/no-unknown-property */
          download={ `${ getFileName(filing, file) }.csv` }
          target="_self"
        >
          { contentCell }
        </a>
      );
    } else {
      cellProps.tooltip = 'CSV not available, download XML';
    }
  }

  return <Cell { ...cellProps } />;
};

const getXmlCell = ({ row: filing }: { row: IFilling }) => {
  const contentCell = <IconFileBrackets />;
  const cellProps = {
    tooltip: 'Related Datatape is not available',
    href: '',
    className: filingsCellsStyles.downloadLink,
    content: <span className={ filingsCellsStyles.disabled }>{ contentCell }</span>,
  };
  const file = getFile(filing);

  if (Object.keys(file).length) {
    cellProps.tooltip = 'Download Data Tape (XML)';
    cellProps.content = (
      <a
        href={ file.url }
        /* eslint-disable-next-line react/no-unknown-property */
        download={ file.name }
        target="_self"
      >
        { contentCell }
      </a>
    );
  }

  return <Cell { ...cellProps } />;
};

const getRelatedDocumentCell = ({ row: filing }: { row: IFilling }) => {
  const contentCell = <IconFileTxt2 />;
  const cellProps = {
    tooltip: 'Related Document is not available',
    href: '',
    className: filingsCellsStyles.downloadLink,
    content: <span className={ filingsCellsStyles.disabled }>{ contentCell }</span>,
  };
  const relatedDocument = getRelatedDocument(filing);

  if (Object.keys(relatedDocument).length) {
    if (isConvertible(relatedDocument)) {
      cellProps.tooltip = 'Download Related Doc (CSV)';
      cellProps.content = (
        <a
          href={ filing.xml2csvUrlTpl.replace('{id}', relatedDocument.fileViewId) }
          /* eslint-disable-next-line react/no-unknown-property */
          download={ `${ getFileName(filing, relatedDocument) }.csv` }
          target="_self"
        >
          <IconFileTable />
        </a>
      );
    } else {
      cellProps.tooltip = 'Download Related Doc (XML)';
      cellProps.content = (
        <a
          href={ relatedDocument.url }
          /* eslint-disable-next-line react/no-unknown-property */
          download={ relatedDocument.name }
          target="_self"
        >
          <IconFileTxt2 />
        </a>
      );
    }
  }

  return <Cell { ...cellProps } />;
};

const getFilingDetailLinkCell = ({ row: filing }: { row: IFilling }) => {
  const cellProps = {
    tooltip: filing.type,
    href: '',
    className: filingsCellsStyles.downloadLink,
    content: (
      <a
        href={ filing.url }
        target="_blank"
      >
        <IconExternalLink />
      </a>
    ),
  };
  return <Cell { ...cellProps } />;
};

const getCell = (prop) => (
  <div className={ filingsCellsStyles.downloadLinksGroup }>
    <span className={ spacesStyles.mrm }>
      { getCsvCell(prop) }
    </span>
    <span className={ cn(spacesStyles.mlm, spacesStyles.mrm) }>
      { getXmlCell(prop) }
    </span>
    <span className={ cn(spacesStyles.mlm, spacesStyles.mrm) }>
      { getRelatedDocumentCell(prop) }
    </span>
    <span className={ spacesStyles.mlm }>
      { getFilingDetailLinkCell(prop) }
    </span>
  </div>
);

interface IDownloadLinksCellPropTypes {
  customClassName: string,
  rowIndex: number,
  filing: IFilling,
}

const DownloadLinksCell = ({
  rowIndex,
  filing,
  customClassName,
}: IDownloadLinksCellPropTypes) => (
  <BodyCell
    name="DownloadLinksCell"
    dataTest="downloadLinksCell"
    className={
      cn(
        filingsCellsStyles.unstyledLink,
        filingsCellsStyles.mainField,
        filingsCellsStyles.downloadableField,
        filingsCellsStyles.noWrap,
        customClassName,
      )
    }
    rowIndex={ rowIndex }
    rowData={ filing }
    cellCallback={ getCell }
  />
);

export default DownloadLinksCell;
